import * as React from 'react';
import { Translate } from 'react-redux-i18n';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import Toggle from '../../Toggle';
import {
	Wrapper,
	Header,
	ButtonClose,
	Loader,
	NoTemplatesLabel,
} from './styles';
import HeaderLogo from '../../../containers/MenuManager/Header/Logo';
import DriveItemList from '../../DriveItemList';
import { getProposalTemplates } from '../../../containers/ProposalCreator/actions';
import { LoadingStatus, SelectedTemplate } from '../../../containers/ProposalCreator/reducer';
import { ListDisplayType } from '../../../containers/DriveItemList/constants';
import { DriveItemInfo } from '../../../containers/DriveItemList/reducer';
import { Api } from '../../../typings/api';
import Utils from '../../../utils';
import useResetDrivePage from '../../../utils/customHooks/useResetDrivePage';

interface ProposalTemplatesProps {
	isDisplayed: boolean;
	loadingStatus: LoadingStatus;
	masterProposalsIds: number[];
	customTemplatesIds: number[];
	driveItems: { [k: number]: DriveItemInfo };
	onClose: () => void;
	onSelect: (item: SelectedTemplate) => void;
	dispatch: Dispatch<StoreState>;
}

enum TemplateSource {
	MASTER_PROPOSALS = 'MASTER_PROPOSALS',
	CUSTOM_TEMPLATES = 'CUSTOM_TEMPLATES',
}

const templateSourceConfig = {
	[TemplateSource.MASTER_PROPOSALS]: {
		id: TemplateSource.MASTER_PROPOSALS,
		label: 'messages.templates',
	},
	[TemplateSource.CUSTOM_TEMPLATES]: {
		id: TemplateSource.CUSTOM_TEMPLATES,
		label: 'messages.my_templates',
	},
};

const ProposalTemplates = ({
	isDisplayed,
	loadingStatus,
	masterProposalsIds,
	customTemplatesIds,
	driveItems,
	onClose,
	onSelect,
	dispatch,
}: ProposalTemplatesProps) => {
	const [templateSource, setTemplateSource] = React.useState(TemplateSource.MASTER_PROPOSALS);
	const shouldResetDrivePage = useResetDrivePage([templateSource]);

	React.useEffect(() => {
		if (isDisplayed && loadingStatus === LoadingStatus.NOT_LOADED) {
			dispatch(getProposalTemplates());
		}

		if (loadingStatus === LoadingStatus.LOADED && masterProposalsIds.length === 0 && customTemplatesIds.length > 0) {
			setTemplateSource(TemplateSource.CUSTOM_TEMPLATES);
		}
	}, [isDisplayed, loadingStatus]);

	const handleTemplateSelect = ({ id, label }: Api.DriveItem): void => {
		onSelect({
			id,
			label,
			isCustomTemplate: templateSource === TemplateSource.CUSTOM_TEMPLATES,
		});
	};

	const hasTemplates = templateSource === TemplateSource.MASTER_PROPOSALS
		? masterProposalsIds.length > 0
		: customTemplatesIds.length > 0;
	const templatesIdsToDisplay = templateSource === TemplateSource.MASTER_PROPOSALS
		? masterProposalsIds
		: customTemplatesIds;

	return (
		<Wrapper>
			<Header>
				<HeaderLogo isDisabled={true} />
				<ButtonClose
					type="button"
					onClick={onClose}
				>
					<Translate value="messages.cancel" />
				</ButtonClose>
			</Header>
			{loadingStatus === LoadingStatus.LOADING &&
				<Loader>
					{Utils.getSvgIcon('iconset_loading')}
					<Translate value="messages.loading" />
				</Loader>
			}
			{loadingStatus === LoadingStatus.LOADED &&
				<>
					<Toggle
						items={Object.values(templateSourceConfig)}
						activeId={templateSource}
						onItemClick={id => setTemplateSource(id as TemplateSource)}
					/>
					{hasTemplates
						? (
							<DriveItemList
								hasBottomBar={false}
								displayType={ListDisplayType.Grid}
								storeDriveItems={driveItems}
								driveItemIdsToDisplay={templatesIdsToDisplay}
								sharedDirId={undefined}
								isPrivateDir={false}
								isFetching={false}
								showUnreadBadge={false}
								showSearchField={false}
								hasSelectionIcon={false}
								hasActions={templateSource === TemplateSource.CUSTOM_TEMPLATES}
								navigationItems={[]}
								isEditMode={false}
								isEditButtonDisplayed={false}
								isDisplayTypeDropdownDisplayed={false}
								isLoading={false}
								onDriveItemClick={handleTemplateSelect}
								shouldResetPage={shouldResetDrivePage}
								dispatch={dispatch}
							/>
						)
						: <NoTemplatesLabel value="messages.no_templates" />
					}
				</>
			}
		</Wrapper>
	);
};

export default ProposalTemplates;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ProposalCreator/ProposalTemplates/index.tsx